import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {radius, weight} from '../scale.js';

export type WedgeBv2TakeLook = 'bv2take-draft' | 'bv2take-call' | 'bv2take-remove' | 'bv2take-add' | 'bv2take-add-idle' | 'bv2take-small';

const BV2_TAKE_LOOKS = new Set<string>(['bv2take-draft', 'bv2take-call', 'bv2take-remove', 'bv2take-add', 'bv2take-add-idle', 'bv2take-small']);
export const isWedgeBv2TakeLook = (look: string | undefined): look is WedgeBv2TakeLook => look !== undefined && BV2_TAKE_LOOKS.has(look);

/* Released brief v2 walk-in kit and your-bag controls (prep-brief-v2), off-scale. Draft outreach is a 46px growing button with a 14px radius, no
   border, the accent fill, the primary ink and glow, 15px semibold type. Call is the 46px anchor beside it: 16px side padding, a 1px border and
   no underline. Remove is the 22px round close button inside a lineup chip. Add is the 42px submit of the lineup form with a 12px radius and 14px
   semibold type; it takes the accent fill and the primary ink once the draft has text (bv2take-add) and the field fill and the muted ink
   before (bv2take-add-idle). Small is the 34px button of the bag card (Edit lineup and Swap): 12px side padding, a 10px radius, a 1px border,
   13px semibold type. The caller sets the font family and points the --wedge-bv2-* roles at the brief scope vars; the app hands in the primary
   ink and glow. Outside the brief each role falls back to a base token. All six boxes are below 44px or sit beside a neighbor in the shipped
   screen and had no transparent target there. No look draws one (wedgeBv2TakeDrawsHit). */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
export const wedgeBv2TakeDrawsHit = (look: string): boolean => !isWedgeBv2TakeLook(look);

const row = {display: 'inline-flex', alignItems: 'center'} as const;

export function wedgeBv2TakeStyle(look: WedgeBv2TakeLook): CSSProperties {
  switch (look) {
    case 'bv2take-draft': return {
      flexGrow: 1, height: 46, borderRadius: radius.xl, border: 'none', cursor: 'pointer',
      background: role('acc', T.accent), color: role('primary-ink', T.label), boxShadow: role('primary-glow', 'none'),
      fontSize: 15, fontWeight: weight.semibold, ...row, justifyContent: 'center', gap: 8,
    };
    case 'bv2take-call': return {
      height: 46, padding: '0 16px', borderRadius: radius.xl, border: `1px solid ${role('line2', T.separator)}`, boxSizing: 'border-box',
      background: 'transparent', color: role('text', T.label), cursor: 'pointer', textDecoration: 'none',
      fontSize: 15, fontWeight: weight.semibold, ...row, gap: 8,
    };
    case 'bv2take-remove': return {
      width: 22, height: 22, borderRadius: radius.pill, border: 'none', background: 'transparent', cursor: 'pointer',
      ...row, justifyContent: 'center', color: role('sec', T.labelSec),
    };
    case 'bv2take-add': return {
      height: 42, padding: '0 16px', borderRadius: radius.lg, border: 'none', cursor: 'pointer',
      background: role('acc', T.accent), color: role('primary-ink', T.label), fontSize: 14, fontWeight: weight.semibold, ...row, gap: 6,
    };
    case 'bv2take-add-idle': return {
      height: 42, padding: '0 16px', borderRadius: radius.lg, border: 'none', cursor: 'default',
      background: role('field', T.surfaceAlt), color: role('muted', T.labelSec), fontSize: 14, fontWeight: weight.semibold, ...row, gap: 6,
    };
    case 'bv2take-small': return {
      height: 34, padding: '0 12px', borderRadius: radius.md, border: `1px solid ${role('line2', T.separator)}`,
      background: role('card', T.surfaceAlt), color: role('text', T.label), cursor: 'pointer',
      fontSize: 13, fontWeight: weight.semibold, ...row, gap: 6,
    };
  }
}

export type WedgeBv2TakeFieldVariant = 'wedge-bv2-lineup-field';
export const isWedgeBv2TakeFieldVariant = (variant: unknown): variant is WedgeBv2TakeFieldVariant => variant === 'wedge-bv2-lineup-field';

/* The add field of the brief v2 lineup editor: a growing, shrinkable 42px input with 14px side padding, a 12px radius, a 1px border, the card
   fill, the text ink and 16px type (16px keeps iOS from zooming on focus). The caller sets the font family and points the --wedge-bv2-* roles
   at the brief scope vars; outside the brief each role falls back to a base token. */
export function wedgeBv2TakeFieldStyle(): CSSProperties {
  return {
    flexGrow: 1, minWidth: 0, height: 42, padding: '0 14px', borderRadius: radius.lg, border: `1px solid ${role('line2', T.separator)}`,
    background: role('card', T.surfaceAlt), color: role('text', T.label), fontSize: 16,
  };
}

export type WedgeBv2FindFieldVariant = 'wedge-bv2-find';
export const isWedgeBv2FindFieldVariant = (variant: unknown): variant is WedgeBv2FindFieldVariant => variant === 'wedge-bv2-find';

/* The find field of the brief v2 header: a bare 14px input that grows inside its label pill. It paints no border, no fill and no outline (the label
   draws the pill); it leaves the browser padding and min width alone, as the shipped input does. The caller sets the font family. */
export function wedgeBv2FindFieldStyle(): CSSProperties {
  return {flexGrow: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 14, color: role('text', T.label)};
}
